<template>
  <div :class="['datepicker-wrapper', {'error': !!errorMessage}, {'is-open': isDatepickerOpen}]">
    <label
      v-if="label"
      :for="id"
      :class="['form-field-label', {'focused': hasFocusedLabel}]"
    >
      {{ label }}
    </label>
    <VueDatePicker
      v-model="inputValue"
      input-class-name="datepicker-input"
      position="left"
      :cancel-text="useMicroCopy('form.datepicker.cancel.label')"
      :select-text="useMicroCopy('form.datepicker.select.label')"
      :uid="id"
      :name="name"
      :required="required"
      :locale="config.public['country']"
      :format-locale="getLocaleFormat()"
      format="d MMMM yyyy"
      :enable-time-picker="false"
      :min-date="today"
      :disabled-week-days="disabled_days"
      no-today
      @open="onDatepickerOpen"
      @update:model-value="onDatepickerUpdate"
      @cleared="onDatepickerClear"
      @closed="onDatepickerClose"
    ></VueDatePicker>
  </div>
  <p v-if="!!errorMessage" role="alert" class="form-error">{{ errorMessage }}</p>

  <div v-if="hasSelectedDate" class="slot-picker">
    <fieldset>
      <legend class="slot-picker-head size-m bold">{{ useMicroCopy('form.slot_picker.title') }}</legend>
      <div class="slot-picker-body">
        <div class="slot-picker-grid">
          <div
            v-for="(item, index) in slots"
            :key="index"
          >
            <label class="slot-button">
              <input
                :id="`${id}-slot-${index}`"
                v-model="radioValue"
                :value="item.value"
                :name="`${id}-slots`"
                type="radio"
                @change="handleChange"
              />
              <span class="size-s bold">{{ generateSlotsLabels(slots, index) }}</span> 
            </label>
          </div>
        </div>
        <SimpleButton
          v-if="hasShortSlots"
          class="more-button"
          type="button"
          xl
          @click="handleClick()"
        >
          {{ useMicroCopy('form.slot_picker.more.cta') }}
        </SimpleButton>
      </div>
    </fieldset>
  </div>
</template>

<script setup>
import { useField } from 'vee-validate';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { enGB, enIE, fr, es, nl, de } from 'date-fns/locale';

const props = defineProps({
  label: {
    type: String,
    required: false,
    default: '',
  },
  id: {
    type: String,
    required: false,
    default: '',
  },
  name: {
    type: String,
    required: false,
    default: '',
  },
  required: {
    type: Boolean,
    required: false
  },
  rules: {
    type: [String, Object],
    required: false,
    default: '',
  },
  options: {
    type: Array,
    required: false,
    default: () => {
      return [];
    },
  }
});

const config = useRuntimeConfig();

const label = computed(() => {
  return props.label + (props.required ? ' *' : '');
})

const rules = computed(() => {
  if (props.rules) return props.rules;

  const array = [
    { name: 'required', value: props.required },
  ];

  return array.filter(item => item.value).map(item => item.name).join('|');
});

const {
  value: inputValue,
  errorMessage,
} = useField(props.name, rules.value);

const hasFocusedLabel = ref(false);
const isDatepickerOpen = ref(false);
const hasShortSlots = ref(true);
const hasSelectedDate = ref(false);
const currentHour= ref(null);
const today = new Date().toISOString().split('T')[0];
const disabled_days = props.options.disabled_days.map(item => parseInt(item));
const startTime = props.options.start_time ? props.options.start_time : '0';
const endTime = props.options.end_time ? props.options.end_time : '23';
const duration = props.options.duration_slot ? props.options.duration_slot : 'full';
const slotInputs = ref(null);
const radioValue = ref(null);
const selectedInputValue = ref(null);
const emit = defineEmits(["isDisabledSubmit", "isEnabledSubmit"]);

const generateSlotsValues = (value) => {
  const stringValue = value.toString();
  const cutStringValue = stringValue.split('.')[0];

  if(stringValue.length === 1) {
    return `0${stringValue}:00`;
  } else if(stringValue.length === 2) {
    return `${stringValue}:00`;
  } else if(stringValue.length === 3) {
    return `0${cutStringValue}:30`;
  } else {
    return `${cutStringValue}:30`;
  }
}

const generateSlotsLabels = (slots, index) => {
  let fallbackValue ='';
  const label1 = slots[index]?.value;
  const label1Hours = label1.substring(0,2);
  const label1Minutes = label1.substring(3,5);

  if(hasShortSlots) {
    fallbackValue = duration === 'half' && label1Minutes === '00' ? `${label1Hours}:30` : generateSlotsValues(parseInt(label1Hours) + 1);
  } else {
    fallbackValue = generateSlotsValues(parseInt(endTime));
  }

  const label2 = slots[index + 1]?.value ?? fallbackValue;
  return `${useFormattedTime(`1970-01-01T${label1}`)}  -  ${useFormattedTime(`1970-01-01T${label2}`)}`;
}

const generateSlots = computed(() => {
  let slotsArray = [];

  for(let i = parseInt(startTime); i < parseInt(endTime); duration === 'full' ? i++ : i = i + 0.5) {
    slotsArray.push({'value': generateSlotsValues(i)});
  }

  hasShortSlots.value = slotsArray.length > 3 ? true : false;
  return slotsArray;
});

const filteredSlots = computed(() => {
  let references = [];
  for(let i = currentHour.value; i >= parseInt(startTime); i--) {
    references.push(i.toString().length === 1 ? `0${i.toString()}` : i.toString());
  }

  const filteredSlots = generateSlots.value.filter(item => !checkStartString(item.value, references));
  hasShortSlots.value = filteredSlots.length > 3 ? true : false;

  return filteredSlots;
});

const slots = computed(() => {
  if(currentHour.value !== null) {
    return hasShortSlots.value ? filteredSlots.value.slice(0, 3) : filteredSlots.value;
  } else {
    return hasShortSlots.value ? generateSlots.value.slice(0, 3) : generateSlots.value;
  }
});

const checkStartString = (str, substrs) => {
  return substrs.some(substr => str.startsWith(substr));
}

const getLocaleFormat = () => {
  let locale;

  switch (config.public['country']) {
    case "es":
      locale = es;
      break;

    case "fr":
      locale = fr;
      break;

    case "cl":
      locale = es;
      break;

    case "de":
      locale = de;
      break;

    case "nl":
      locale = nl;
      break;

    case "ie":
      locale = enIE;
      break;
    
    default:
      locale = enGB;
  }

  return locale;
}

const handleClick = () => {
  hasShortSlots.value = false;

  if(document.querySelector('.slot-button input[type="radio"]:checked') === null) {
    setTimeout(() => {
      slotInputs.value = document.querySelectorAll('.slot-button input[type="radio"]');
    
      slotInputs.value.forEach((input) => {
        input.checked = false;
      });
    });
  } else {
    setTimeout(() => {
      slotInputs.value = document.querySelectorAll('.slot-button input[type="radio"]');
    });
  }
}

const handleChange = () => {
  inputValue.value = selectedInputValue.value;
  emit('isEnabledSubmit');

  const timePart = radioValue.value;
  const datePart = inputValue.value.toISOString().split('T')[0];

  inputValue.value = `${datePart}T${timePart}:00`;
}

const onDatepickerUpdate = (modelData) => {
  currentHour.value = null;
  hasShortSlots.value = true;
  
  setTimeout(() => {
    slotInputs.value = document.querySelectorAll('.slot-button input[type="radio"]');

    slotInputs.value.forEach((input) => {
      input.checked = false;
    });
  });
  
  hasFocusedLabel.value = modelData === null ? false : true;
  inputValue.value = modelData;
  hasSelectedDate.value = modelData !== null ? true : false;
  selectedInputValue.value = inputValue.value;

  emit('isDisabledSubmit');

  if(modelData?.setHours(0,0,0,0) === new Date().setHours(0,0,0,0)) { 
    currentHour.value = new Date().getHours();
  }
}

const onDatepickerOpen = () => {
  hasFocusedLabel.value = true;
  isDatepickerOpen.value = true;
}

const onDatepickerClear = () => {
  hasFocusedLabel.value = false;
  emit('isEnabledSubmit');
}

const onDatepickerClose = () => {
  hasFocusedLabel.value = inputValue.value === null ? false : true;
  isDatepickerOpen.value = false;
}

</script>
<style lang="scss" scoped>
$input-height-mobile: 4rem;
$input-height-desktop: 5rem;

.datepicker-wrapper {
  height: $input-height-mobile;

  @include for-tablet-landscape-up {
    height: $input-height-desktop;
  }
  
  .form-field-label {
    z-index: 1;

    &.focused {
      top: 0.5rem;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 140%;
      color: var(--text-100);
    }
  }

  &.error :deep(.datepicker-input) {
    background-color: var(--form-field-error);
  }
}

:deep(.dp__main) {
  z-index: unset;

  & > div {
    z-index: unset;
  }

  & > div + div {
    margin-top: - $input-height-mobile;

    @include for-tablet-landscape-up {
      margin-top: - $input-height-desktop;
    }
  }
}

:deep(.datepicker-input) {
  @include pair-5;
  border: none;
  height: $input-height-mobile;
  border-radius: $radius-xs;
  font-family: "Manrope", sans-serif;
  font-weight: 700;
  padding: 0.625rem 1.125rem;
  @include size-m;
  position: relative;

  @include for-tablet-landscape-up {
    height: $input-height-desktop;
    border-radius: $radius-l;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  &.dp__input_focus {
    outline: 2px solid var(--text-50);
  }
}

.form-field-label.focused + :deep(.dp__main) {
  .dp__input_wrap > div {
    display: none;
  }
}

:deep(.dp__input_wrap) > div {
  position: absolute;
  top: 50%;
  right: 1.125rem;

  @include for-tablet-landscape-up {
    right: 1.5rem;
  }

  .dp__icon {
    position: static;
    width: 1.5rem;
    height: 1.5rem;
  }
}

:deep(.dp__clear_icon) {
  margin-right: 1rem;
  color: var(--text-100);
}

:deep(.dp__action_buttons .dp__action_select),
:deep(.dp__active_date) {
  background: var(--brand);
}

.slot-picker {
  margin: 0.625rem 0;
  z-index: -1;

  &-head {
    display: flex;
    align-items: center;
    border-radius: $radius-m $radius-m 0 0;
    border: 1px solid rgba(0, 0, 0, 0.5);
    background-color: var(--solid-05);
    width: 100%;
    height: 5rem;
    padding: 0 1.5rem;
  }

  &-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 0 0 $radius-m $radius-m;
    border: 1px solid rgba(0, 0, 0, 0.5);
    border-top: none;
    padding: 1.5rem;
  }

  &-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 0.625rem;
    width: 100%;

    @include for-tablet-landscape-up {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .slot-button {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: $radius-l;
    border: 1px solid rgba(0, 0, 0, 0.5);
    background-color: var(--solid-05);
    width: 100%;
    height: 3.125rem;
    cursor: pointer;

    &:has(input:checked) {
      color: var(--text-reverse-100);
      background: linear-gradient(
        136deg,
        var(--gradient-start) 0%,
        var(--gradient-end) 100%
      );
      border-color: transparent;
      cursor: default;
    }

    input {
      position: absolute;
      top: 0;
      left: -999em;
    }
  }

  .more-button {
    --parent-bg: var(--solid-07);
    margin-top: 2rem;
  }
}
</style>